/**
 * Platform-aware payment router.
 *
 * Apple App Store & Google Play forbid third-party payment gateways for
 * digital goods inside native apps. When this web app is wrapped by Despia
 * (Capacitor-compatible shell), digital purchases MUST flow through
 * RevenueCat (Apple IAP / Google Play Billing). On web, all gateways are
 * available.
 *
 * Detection order (first match wins):
 *  1. Build-time override: VITE_FORCE_PLATFORM = "web" | "ios" | "android"
 *  2. Capacitor runtime (window.Capacitor) — injected by Despia/Capacitor
 *  3. Despia globals / bridge (window.despia, window.despiaPlatform)
 *  4. UA sniff for Despia / RevenueCat / native WebView markers
 *  5. URL flag (?platform=ios|android) used by the native shell's start URL
 *  6. Sticky value persisted from any earlier detection in this install
 *  7. Fallback: "web"
 *
 * Native detection is STICKY: once a session is identified as iOS/Android it
 * stays native for the rest of the install, so a late-loading bridge can never
 * expose third-party gateways for digital goods inside the store apps.
 */

export type PaymentPlatform = "web" | "ios" | "android";

declare global {
  interface Window {
    Capacitor?: {
      getPlatform?: () => string;
      isNativePlatform?: () => boolean;
    };
    despia?: unknown;
    despiaPlatform?: string;
    Purchases?: unknown;
    webkit?: { messageHandlers?: Record<string, unknown> };
  }
}

const STICKY_KEY = "gg_native_platform";

function readSticky(): PaymentPlatform | null {
  try {
    const v = localStorage.getItem(STICKY_KEY);
    return v === "ios" || v === "android" ? v : null;
  } catch {
    return null;
  }
}

function writeSticky(p: PaymentPlatform) {
  if (p === "web") return;
  try { localStorage.setItem(STICKY_KEY, p); } catch { /* private mode */ }
}

function osFromUa(ua: string): PaymentPlatform {
  if (/Android/i.test(ua)) return "android";
  if (/iPhone|iPad|iPod|iOS|Macintosh/i.test(ua)) return "ios";
  return "ios"; // native shell of unknown OS — treat as store-restricted
}

function detectPlatform(): PaymentPlatform {
  const forced = (import.meta as any)?.env?.VITE_FORCE_PLATFORM as
    | PaymentPlatform
    | undefined;
  if (forced === "web" || forced === "ios" || forced === "android") return forced;

  if (typeof window === "undefined") return "web";

  const ua = navigator.userAgent || "";

  // 2. Capacitor runtime
  const cap = window.Capacitor;
  if (cap?.getPlatform) {
    const p = cap.getPlatform();
    if (p === "ios" || p === "android") return p;
  }
  if (cap?.isNativePlatform?.()) return osFromUa(ua);

  // 3. Despia globals
  const dp = (window.despiaPlatform || "").toLowerCase();
  if (dp === "ios" || dp === "android") return dp as PaymentPlatform;
  if (typeof window.despia !== "undefined") return osFromUa(ua);

  // 4. UA / native WebView markers
  if (/Despia|RevenueCat|GonativeIO|Median|wv\)/i.test(ua)) return osFromUa(ua);

  // 5. Explicit URL flag from the native shell's start URL
  try {
    const q = new URLSearchParams(window.location.search).get("platform");
    if (q === "ios" || q === "android") return q;
  } catch { /* ignore */ }

  // 6. Sticky value from an earlier detection in this install
  const sticky = readSticky();
  if (sticky) return sticky;

  return "web";
}

const platform: PaymentPlatform = detectPlatform();
writeSticky(platform);

/** Re-evaluate once the native bridge finishes injecting (Despia can be late). */
if (typeof window !== "undefined" && platform === "web") {
  let tries = 0;
  const poll = window.setInterval(() => {
    tries += 1;
    const again = detectPlatform();
    if (again !== "web") {
      writeSticky(again);
      window.clearInterval(poll);
      // Reload so every gateway-gated view re-renders under native rules.
      window.location.reload();
    }
    if (tries > 10) window.clearInterval(poll);
  }, 500);
}

export type PaymentMethod =
  | "stripe"
  | "paypal"
  | "paystack"
  | "flutterwave"
  | "wallet"
  | "revenuecat";

export const PaymentRouter = {
  platform,
  isWeb: platform === "web",
  isMobileNative: platform === "ios" || platform === "android",

  /** Digital goods & subscriptions — store-compliant. */
  methods(): PaymentMethod[] {
    if (platform === "web") return ["stripe", "paypal", "paystack", "flutterwave", "wallet"];
    return ["revenuecat"];
  },

  /** Physical goods & real-world services (rides, hotels, marketplace
   *  shipping). Third-party processors are allowed on native per policy. */
  methodsForPhysicalGoods(): PaymentMethod[] {
    return ["stripe", "paypal", "paystack", "flutterwave", "wallet"];
  },

  // Convenience booleans for JSX gating of DIGITAL goods
  showStripe: platform === "web",
  showPayPal: platform === "web",
  showPaystack: platform === "web",
  showFlutterwave: platform === "web",
  showWallet: platform === "web",
  showRevenueCat: platform === "ios" || platform === "android",
} as const;

export const isNativeWrapper = PaymentRouter.isMobileNative;
